import * as THREE from 'three';
import { Core } from './Core';

export class LightingService {
	/**
	 * System Properties
	 * */
	core: Core;

	/**
	 * Objects
	 * */
	lights: Light[] = [];
	lightHelpers: any[] = [];

	/**
	 * Constructor
	 * */
	constructor(core: Core) {
		this.core = core;
	}

	/**
	 * Adds an ambient light
	 * */
	createAmbientLight(name: string, color: string = '#ffffff', intensity: number = 0.5) {
		const threeLight = new THREE.AmbientLight(color, intensity);
		threeLight.name = name;
		const ambientLight = new Light(name, threeLight);
		
		this.lights.push(ambientLight);

		if (this.core.debugMode) this.core.s('Debug').debugLight(ambientLight, 'Ambient Light');

		return ambientLight;
	}

	/**
	 * Adds a directional light
	 * */
	createDirectionalLight(
		name: string,
		position: THREE.Vector3 = new THREE.Vector3(40, 40, 40),
		color: string = '#ffffff',
		intensity: number = 1.25
	) {
		const threeLight = new THREE.DirectionalLight(color, intensity);
		threeLight.name = name;
		threeLight.position.set(position.x, position.y, position.z);
		const directionalLight = new Light(name, threeLight);
		
		this.lights.push(directionalLight);

		if (this.core.debugMode) this.core.s('Debug').debugLight(directionalLight, 'Directional Light');

		return directionalLight;
	}

	/**
	 * Adds a point light
	 * */
	createPointLight(
		name: string,
		position: THREE.Vector3 = new THREE.Vector3(0, 5, 0),
		color: string = '#ffffff',
		intensity: number = 1,
		distance: number = 10,
		decay: number = 0.5
	) {
		const threeLight = new THREE.PointLight(color, intensity, distance, decay);
		threeLight.name = name;
		threeLight.position.set(position.x, position.y, position.z);
		const pointLight = new Light(name, threeLight);
		
		this.lights.push(pointLight);

		return pointLight;
	}

	/**
	 * Shows Light Helpers
	 * */
	addLightHelpers() {
		this.lights.forEach((light) => {
			let helper;
			switch (true) {
				case light instanceof THREE.DirectionalLight:
					helper = new THREE.DirectionalLightHelper(light.threeLight as THREE.DirectionalLight, 5);
					this.core.threeScene.add(helper);
					break;
			}
		});
	}

	/**
	 * Makes a light cast shadows
	 * */
	addShadowsToLight(light: Light) {
		light.threeLight.castShadow = true;

		if (light.threeLight.shadow) {
			light.threeLight.shadow.mapSize.width = 2048;
			light.threeLight.shadow.mapSize.height = 2048;
			(light.threeLight.shadow.camera as any).left = 250;
			(light.threeLight.shadow.camera as any).right = -250;
			(light.threeLight.shadow.camera as any).bottom = -250;
			(light.threeLight.shadow.camera as any).top = 250;
			(light.threeLight.shadow.camera as any).near = 0.5;
			(light.threeLight.shadow.camera as any).far = 250;
			//light.threeLight.shadow.radius = 10; // Adds 'blur' to shadows
			//(light.threeLight.shadow as any).type = THREE.PCFSoftShadowMap;
			light.threeLight.shadow.normalBias = 0.4; // 0.03
		}
	}

	/**
	 * Stops a light casting shadows
	 * */
	removeShadowsFromLight(light: Light) {
		light.threeLight.castShadow = false;
	}

	/**
	 * Returns the main light
	 * */
	getLightByName(name: string) {
		return this.lights.find((light) => light.name == name);
	}

	/**
	 * Adds the light to the scene
	 * @param { string | Light } lightToEnable Name of the light as a string, or an actual light
	 * */
	enableLight(lightToEnable: string | Light) {
		let light = null;

		if (typeof (lightToEnable) == "string") {
			light = this.getLightByName(lightToEnable);
		} else if (lightToEnable instanceof Light) {
			light = lightToEnable;
		}

		if (light) this.core.threeScene.add(light.threeLight);
	}

	/**
	 * Removes the light to the scene
	 * */
	disableLight(name: string) {
		const light = this.getLightByName(name);
		if (light) this.core.threeScene.remove(light.threeLight);
	}

	/**
	 * Remove Light Helpers
	 * */
	removeLightHelpers() {
		this.lightHelpers.forEach((lh) => {
			this.core.threeScene.remove(lh);
			this.lightHelpers = this.lightHelpers.filter((x) => x != lh);
		});
	}
}

export class Light {
	name: string;
	threeLight: THREE.Light;

	constructor(name: string, threeLight: THREE.Light) {
		this.name = name;
		this.threeLight = threeLight;

	}
}
